import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Getting started → Review the draft.
// Fixture: `reviewTheDraft` (the Valley Mart report from lesson 1, finished,
// opened directly). The scene never uploads anything.
const REVIEW_CASE_ID = "c0000000-0000-4000-8000-000000000201";
const PLACEHOLDER = `${sel("editorNarrative")} .replace-text`;

const scene: Scene = {
  id: "review-the-draft",
  title: "Review the draft",
  startRoute: `/cases/${REVIEW_CASE_ID}`,
  fixture: "reviewTheDraft",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "A draft isn't finished the moment it appears. Three things tell you what's left: the score, the suggestions, and the placeholders. Here's how to work through them, using the report from the last lesson.",
      actions: [
        { type: "waitFor", target: sel("reportScore") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "score",
      narration:
        "The score at the top of the Suggestions tab tells you how complete and clear the report is. This one is Excellent. It isn't about length, so a short report can still score high.",
      actions: [
        { type: "moveTo", target: sel("reportScore") },
        { type: "spotlight", target: sel("reportScore") },
        { type: "wait", ms: 6500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "suggestions",
      narration:
        "Under the score are the gaps. Each suggestion is one specific thing a supervisor would ask about, like when you arrived, or which way the suspect left. Check one off when you've dealt with it.",
      actions: [
        { type: "moveTo", target: sel("suggestionItem") },
        { type: "spotlight", target: sel("suggestionItem") },
        { type: "wait", ms: 10200 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "placeholders",
      narration:
        "Where the recording never gave a fact, the draft leaves a placeholder instead of guessing. This highlighted one is the arrival time. If the toggle is on, Hide placeholders collapses them so you can read the narrative clean.",
      actions: [
        // Timed to the clip: "This highlighted one" starts at 5.2 s, the
        // toggle is named by 8.9 s, and the narration ends at 12.5 s.
        { type: "scrollIntoView", target: PLACEHOLDER },
        { type: "moveTo", target: PLACEHOLDER },
        { type: "wait", ms: 3900 },
        { type: "spotlight", target: PLACEHOLDER },
        { type: "wait", ms: 2300 },
        { type: "spotlight", target: null },
        { type: "moveTo", target: `${sel("hidePlaceholders")} input` },
        { type: "wait", ms: 700 },
        { type: "click", target: `${sel("hidePlaceholders")} input` },
        { type: "wait", ms: 1800 },
        { type: "click", target: `${sel("hidePlaceholders")} input` },
        { type: "waitFor", target: PLACEHOLDER },
        { type: "wait", ms: 1400 },
      ],
    },
    {
      id: "fix",
      narration:
        "To fix a placeholder, click it and type the real value. I'll put in fourteen thirty-seven hours.",
      actions: [
        // Typing starts as "I'll put in" is said (3.8 s of the 6.2 s clip).
        { type: "moveTo", target: PLACEHOLDER },
        { type: "wait", ms: 3300 },
        { type: "typeOver", target: PLACEHOLDER, text: "1437 hours", cps: 9 },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "re-evaluate",
      narration:
        "Then hit Re-evaluate. The score updates, and the suggestion you just answered drops off the list.",
      actions: [
        { type: "moveTo", target: sel("reEvaluate") },
        { type: "wait", ms: 150 },
        { type: "click", target: sel("reEvaluate") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: sel("reportScore") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "address",
      narration:
        "When there are several gaps, don't type them one by one. Address Suggestions lets you record or upload audio, and the AI writes your answers into the right places for you to review.",
      actions: [
        // "Address Suggestions" is said at 3.5 s; the menu stays open while
        // the rest of the sentence (record or upload audio) plays.
        { type: "moveTo", target: sel("addToReportButton") },
        { type: "wait", ms: 2800 },
        { type: "click", target: `${sel("addToReportButton")} button` },
        { type: "wait", ms: 5500 },
        { type: "click", target: sel("reportScore") },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "outro",
      narration:
        "One last thing. An exported report still contains any placeholder you left in it, so clear them all before you export.",
      actions: [
        { type: "moveTo", target: sel("editorExport") },
        { type: "spotlight", target: sel("editorExport") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
